/**
 * ResetPassword — iter-121.7 (Feb 2026).
 *
 * Landing page for the secure password-reset link emailed to the user.
 * Three states:
 *   1. validating  — checking the token on mount.
 *   2. invalid     — token expired / used / unknown → CTA back to /login.
 *   3. form        — show user's email + new-password / confirm fields;
 *                    POST /api/auth/reset-password/confirm; on 200 →
 *                    redirect to /login with a one-time toast.
 *
 * Password rules + minimum-length come from the live policy endpoint
 * so any change in Settings → Security takes effect with no rebuild.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, ShieldCheck, AlertCircle } from "lucide-react";

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";
    const navigate = useNavigate();

    const [stage, setStage] = useState("validating"); // validating | invalid | form
    const [email, setEmail] = useState("");
    const [policy, setPolicy] = useState({ password_min_length: 10, password_required_classes: 3 });
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState("");

    useEffect(() => {
        if (!token) { setStage("invalid"); return; }
        let cancelled = false;
        (async () => {
            try {
                const [v, p] = await Promise.all([
                    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`),
                    api.get("/auth/signup/policy").catch(() => ({ data: {} })),
                ]);
                if (cancelled) return;
                if (!v.data?.valid) {
                    setStage("invalid");
                } else {
                    setEmail(v.data.email || "");
                    setPolicy({
                        password_min_length: p.data?.password_min_length || 10,
                        password_required_classes: p.data?.password_required_classes || 3,
                    });
                    setStage("form");
                }
            } catch (_e) {
                if (!cancelled) setStage("invalid");
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    // Live password-policy hints — same logic as Signup so the UX is consistent.
    const pwState = useMemo(() => {
        const lower = /[a-z]/.test(password), upper = /[A-Z]/.test(password);
        const digit = /\d/.test(password), symbol = /[^a-zA-Z0-9]/.test(password);
        const lengthOk = password.length >= (policy.password_min_length || 10);
        const classesHit = [lower, upper, digit, symbol].filter(Boolean).length;
        const classesOk = classesHit >= (policy.password_required_classes || 3);
        const matchesConfirm = password.length > 0 && password === confirm;
        return { lengthOk, classesOk, classesHit, matchesConfirm, strongEnough: lengthOk && classesOk && matchesConfirm };
    }, [password, confirm, policy]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!pwState.strongEnough || submitting) return;
        setServerError("");
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password/confirm", {
                token,
                new_password: password,
                email: email || undefined,
            });
            toast.success("Password updated — you can now sign in with your new password.");
            navigate("/login", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Could not reset your password. The link may have expired — please request a new one.";
            setServerError(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-background p-6" data-testid="reset-password-page">
            <div className="w-full max-w-md border border-border bg-card rounded-sm p-7">
                <div className="eyebrow mb-1.5">Account security</div>
                <h1 className="text-2xl font-medium leading-tight">Reset your password</h1>

                {stage === "validating" && (
                    <div className="mt-6 text-sm text-muted-foreground" data-testid="reset-validating">
                        Checking your reset link…
                    </div>
                )}

                {stage === "invalid" && (
                    <div className="mt-6 space-y-4" data-testid="reset-invalid">
                        <div className="flex items-start gap-3 p-3 border border-destructive/30 bg-destructive/5 rounded-sm">
                            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                            <div className="text-sm text-foreground/90">
                                This reset link is no longer valid. It may have expired, been used, or never existed.
                                Request a new link from the login page.
                            </div>
                        </div>
                        <Button onClick={() => navigate("/login")} className="w-full rounded-sm" data-testid="reset-back-to-login">
                            Back to sign in
                        </Button>
                    </div>
                )}

                {stage === "form" && (
                    <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="reset-form">
                        <div className="p-3 border border-border bg-secondary/40 rounded-sm flex items-start gap-3">
                            <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                            <div className="text-xs">
                                <div className="text-muted-foreground">Resetting password for</div>
                                <div className="font-mono text-foreground mt-0.5" data-testid="reset-email">{email}</div>
                            </div>
                        </div>

                        <div>
                            <label className="eyebrow mb-1.5 block">New password</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    required value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="new-password"
                                    className="w-full h-10 pl-3 pr-9 bg-transparent border border-border rounded-sm text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                                    data-testid="reset-password-input"
                                />
                                <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                                    {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                </button>
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1.5 space-y-0.5">
                                <div className={pwState.lengthOk ? "text-[hsl(var(--success))]" : ""}>
                                    • {pwState.lengthOk ? "✓" : "○"} At least {policy.password_min_length} characters
                                </div>
                                <div className={pwState.classesOk ? "text-[hsl(var(--success))]" : ""}>
                                    • {pwState.classesOk ? "✓" : "○"} At least {policy.password_required_classes} of: lowercase, uppercase, digit, symbol ({pwState.classesHit}/4)
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="eyebrow mb-1.5 block">Confirm new password</label>
                            <input
                                type={showPassword ? "text" : "password"}
                                required value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                autoComplete="new-password"
                                className={
                                    "w-full h-10 pl-3 pr-3 bg-transparent border rounded-sm text-sm font-mono focus:outline-none focus:ring-1 "
                                    + (confirm.length > 0 && !pwState.matchesConfirm
                                        ? "border-destructive focus:ring-destructive"
                                        : "border-border focus:ring-primary")
                                }
                                data-testid="reset-confirm-input"
                            />
                            {confirm.length > 0 && !pwState.matchesConfirm && (
                                <div className="text-[10px] text-destructive mt-1" data-testid="reset-confirm-mismatch">
                                    Passwords don't match
                                </div>
                            )}
                        </div>

                        {serverError && (
                            <div className="text-xs text-destructive border border-destructive/30 bg-destructive/5 rounded-sm p-2.5" data-testid="reset-server-error">
                                {serverError}
                            </div>
                        )}

                        <Button
                            type="submit"
                            className="w-full rounded-sm"
                            disabled={!pwState.strongEnough || submitting}
                            data-testid="reset-submit"
                        >
                            {submitting ? "Updating…" : "Reset password"}
                        </Button>

                        <div className="text-center">
                            <button type="button" onClick={() => navigate("/login")} className="text-xs text-muted-foreground hover:text-foreground underline" data-testid="reset-cancel">
                                Cancel and return to sign in
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
